import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import style from '../../../../scss/components/form.module.scss';
import '../../../../scss/external/select.scss';
import Field from './Field';

const toOption = (tag) => {
    if (!tag) return null;

    return {
        value: tag.id ?? tag.uuid ?? tag.value,
        label: tag.name ?? tag.title ?? tag.label,
        ...tag,
    };
};

const normalizeSelected = (value) => {
    if (!value) {
        return [];
    }

    if (!Array.isArray(value)) {
        return [toOption(value)].filter(Boolean);
    }

    return value.map(toOption).filter(Boolean);
};

const optionKey = (option) =>
    option?.value ?? option?.id ?? option?.uuid ?? null;

const areOptionsEqual = (a, b) => {
    const arrA = Array.isArray(a) ? a : [];
    const arrB = Array.isArray(b) ? b : [];

    if (arrA.length !== arrB.length) {
        return false;
    }

    return arrA.every(
        (item, index) => optionKey(item) === optionKey(arrB[index]),
    );
};

const typeValue = (type) => {
    if (!type) {
        return null;
    }

    if (typeof type === 'string') {
        return type;
    }

    return type.value ?? type.id ?? type.name ?? type.label ?? null;
};

const typeLabel = (type) => {
    if (!type) {
        return '';
    }

    if (typeof type === 'string') {
        return type;
    }

    return type.label ?? type.name ?? type.value ?? type.id ?? '';
};

const getNestedValue = (object, path) => {
    if (!object || !path) {
        return null;
    }

    return path.split('.').reduce((value, key) => value?.[key], object);
};

const getTagType = (tag, typeFieldName) => {
    const type = getNestedValue(tag, typeFieldName);

    if (!type) {
        return null;
    }

    return typeValue(type);
};

const getResultData = (result) => {
    if (Array.isArray(result)) {
        return result;
    }

    return result?.data ?? [];
};

const buildTypeQuery = (type) => {
    const params = new URLSearchParams();

    params.append('types[]', type);

    return params.toString();
};

const TypedTagInput = ({
    entity,
    onChange,
    service,
    name,
    typeFieldName,
    ...props
}) => {
    const { t } = useTranslation();

    const [types, setTypes] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(() =>
        normalizeSelected(entity[name]),
    );
    const [isTypesLoading, setIsTypesLoading] = useState(false);
    const [isTagsLoading, setIsTagsLoading] = useState(false);
    const [hasInitialisedTypes, setHasInitialisedTypes] = useState(false);

    const entitySelectedTags = useMemo(() => {
        return normalizeSelected(entity[name]);
    }, [entity, name]);

    useEffect(() => {
        if (!areOptionsEqual(selected, entitySelectedTags)) {
            setSelected(entitySelectedTags);
        }
    }, [entitySelectedTags, selected]);

    useEffect(() => {
        if (!entitySelectedTags.length) {
            return;
        }

        setOptions((current) => {
            const existingIds = current.map(optionKey);

            const missingSelectedTags = entitySelectedTags.filter(
                (tag) => !existingIds.includes(optionKey(tag)),
            );

            return [...current, ...missingSelectedTags];
        });
    }, [entitySelectedTags]);

    useEffect(() => {
        let cancelled = false;

        setIsTypesLoading(true);

        service
            .get('tags/types')
            .then((result) => {
                if (cancelled) {
                    return;
                }

                const data = getResultData(result);

                setTypes(
                    data
                        .map((type) => ({
                            value: typeValue(type),
                            label: typeLabel(type),
                        }))
                        .filter((type) => type.value),
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setTypes([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsTypesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [service]);

    useEffect(() => {
        let cancelled = false;

        if (
            hasInitialisedTypes ||
            !types.length ||
            !entitySelectedTags.length
        ) {
            return undefined;
        }

        const availableTypeValues = types.map((type) => type.value);

        const directInitialTypes = entitySelectedTags
            .map((tag) => getTagType(tag, typeFieldName))
            .filter(Boolean)
            .filter((type) => availableTypeValues.includes(type));

        if (directInitialTypes.length) {
            setSelectedTypes([...new Set(directInitialTypes)]);
            setHasInitialisedTypes(true);

            return undefined;
        }

        const selectedTagIds = entitySelectedTags
            .map(optionKey)
            .filter(Boolean);

        if (!selectedTagIds.length) {
            setHasInitialisedTypes(true);

            return undefined;
        }

        setIsTagsLoading(true);

        Promise.all(
            types.map((type) =>
                service
                    .get(`tags?${buildTypeQuery(type.value)}`)
                    .then((result) => ({
                        type: type.value,
                        tags: getResultData(result)
                            .map(toOption)
                            .filter(Boolean),
                    }))
                    .catch(() => ({
                        type: type.value,
                        tags: [],
                    })),
            ),
        )
            .then((results) => {
                if (cancelled) {
                    return;
                }

                const inferredTypes = results
                    .filter((result) =>
                        result.tags.some((tag) =>
                            selectedTagIds.includes(optionKey(tag)),
                        ),
                    )
                    .map((result) => result.type);

                const fetchedOptions = results
                    .flatMap((result) => result.tags)
                    .filter(Boolean);

                setOptions((current) => {
                    const optionIds = current.map(optionKey);

                    const missingOptions = fetchedOptions.filter(
                        (option) => !optionIds.includes(optionKey(option)),
                    );

                    return [...current, ...missingOptions];
                });

                setSelectedTypes([...new Set(inferredTypes)]);
                setHasInitialisedTypes(true);
            })
            .finally(() => {
                if (!cancelled) {
                    setIsTagsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        hasInitialisedTypes,
        types,
        entitySelectedTags,
        typeFieldName,
        service,
    ]);

    useEffect(() => {
        let cancelled = false;

        if (!selectedTypes.length) {
            setOptions((current) => {
                const existingIds = current.map(optionKey);

                const missingSelectedTags = entitySelectedTags.filter(
                    (tag) => !existingIds.includes(optionKey(tag)),
                );

                return [...current, ...missingSelectedTags];
            });

            return undefined;
        }

        const params = new URLSearchParams();

        selectedTypes.forEach((type) => {
            params.append('types[]', type);
        });

        setIsTagsLoading(true);

        service
            .get(`tags?${params.toString()}`)
            .then((result) => {
                if (cancelled) {
                    return;
                }

                const data = getResultData(result);
                const mappedOptions = data.map(toOption).filter(Boolean);

                setOptions((current) => {
                    const existingIds = mappedOptions.map(optionKey);

                    const missingSelectedTags = current.filter(
                        (tag) => !existingIds.includes(optionKey(tag)),
                    );

                    return [...mappedOptions, ...missingSelectedTags];
                });

                setSelected((current) => {
                    const allowedIds = mappedOptions.map(optionKey);

                    const filteredSelected = current.filter((tag) =>
                        allowedIds.includes(optionKey(tag)),
                    );

                    if (!areOptionsEqual(current, filteredSelected)) {
                        onChange({
                            [name]: filteredSelected,
                        });
                    }

                    return filteredSelected;
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setOptions(entitySelectedTags);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsTagsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [service, selectedTypes, name, onChange, entitySelectedTags]);

    const toggleType = (type) => {
        setSelectedTypes((current) => {
            if (current.includes(type)) {
                return current.filter((item) => item !== type);
            }

            return [...current, type];
        });
    };

    const handleTagChange = (value) => {
        const nextSelected = normalizeSelected(value);

        setSelected(nextSelected);

        onChange({
            [name]: nextSelected,
        });
    };

    const isLoading = isTypesLoading || isTagsLoading;

    return (
        <div className={classNames(style.formGroup, props.className)}>
            <Field {...props} name={name}>
                {(hasError, attributes, isChanged) => (
                    <>
                        <div className={style.checkboxGroup}>
                            {types.map((type) => (
                                <label
                                    key={type.value}
                                    className={style.checkboxLabel}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedTypes.includes(
                                            type.value,
                                        )}
                                        onChange={() => toggleType(type.value)}
                                    />
                                    <span>{type.label}</span>
                                </label>
                            ))}
                        </div>

                        <Select
                            inputId={attributes.id}
                            name={name}
                            isMulti
                            isClearable
                            isLoading={isLoading}
                            isDisabled={!selectedTypes.length}
                            options={options}
                            value={selected}
                            onChange={handleTagChange}
                            placeholder={t('tags.placeholder')}
                            className={classNames(
                                style.autocomplete,
                                `-${name}`,
                                { [style.changedInput]: isChanged },
                                { [style.isInvalid]: hasError },
                            )}
                            classNamePrefix="rs"
                            menuPlacement="auto"
                        />
                    </>
                )}
            </Field>
        </div>
    );
};

TypedTagInput.propTypes = {
    entity: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    service: PropTypes.object.isRequired,
    name: PropTypes.string,
    className: PropTypes.string,
    typeFieldName: PropTypes.string,
};

TypedTagInput.defaultProps = {
    name: 'tags',
    className: '',
    typeFieldName: 'type',
};

export default TypedTagInput;
